import React from "react";
import { useTranslation } from "react-i18next";
import { PhoneOutlined } from "@ant-design/icons";
import { QUESTION_TYPE, RATING_SCALE, RATING_SCALE_META } from "./constants";
import styles from "./style.module.scss";

const CHOICE_TYPES = [QUESTION_TYPE.SINGLE_CHOICE, QUESTION_TYPE.MULTIPLE_CHOICE];

const mentionsNumber = (text, n) => new RegExp(`(^|\\D)${n}(\\D|$)`).test(text);

const MERGE_FIELD = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

// A line with its merge fields: a typed test value in place, else the field as a small tag.
export function withFields(text, values) {
  const parts = [];
  let last = 0;
  const pattern = new RegExp(MERGE_FIELD.source, "g");
  for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const value = String((values || {})[match[1]] || "").trim();
    parts.push(
      value ? (
        <span key={match.index} className={styles.voicePreviewFilled}>
          {value}
        </span>
      ) : (
        <span key={match.index} className={styles.voicePreviewField}>
          {match[1]}
        </span>
      )
    );
    last = pattern.lastIndex;
  }
  if (!parts.length) return text;
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// What the agent adds after a question so the customer knows how to answer, unless the
// question already says it ("1 ile 5 arasında…", or every option written out).
function answerHint(question, t) {
  const text = String(question.text || "");
  if (question.type === QUESTION_TYPE.RATING) {
    const meta = RATING_SCALE_META[question.ratingScale] || RATING_SCALE_META[RATING_SCALE.ONE_TO_FIVE];
    if (mentionsNumber(text, meta.min) && mentionsNumber(text, meta.max)) return "";
    return t("surveyManagement.voice_preview_scale", { min: meta.min, max: meta.max });
  }
  if (CHOICE_TYPES.includes(question.type)) {
    const options = (question.options || []).map((o) => String(o || "").trim()).filter(Boolean);
    const lower = text.toLocaleLowerCase();
    if (!options.length || options.every((o) => lower.includes(o.toLocaleLowerCase()))) return "";
    return t("surveyManagement.voice_preview_options", { options: options.join(", ") });
  }
  return "";
}

/**
 * A voice survey's preview: the call as the AI agent runs it — greeting, each question in
 * order with how to answer, and the goodbye — instead of the web form. Merge fields show the
 * tester's ``values`` when typed, else a tag. ``inTabs``: it is the "call preview" tab next to a
 * "test now" tab, so the tab names it, it needs no frame of its own and no pointer to the test.
 */
const VoiceSurveyPreview = ({ questions, greeting, disclosure, thankYouMessage, values, inTabs = false }) => {
  const { t } = useTranslation();
  const asked = (questions || []).filter((q) => q && String(q.text || "").trim());
  const agent = t("surveyManagement.voice_preview_agent");

  const line = (key, text, muted = false) => (
    <div key={key} className={styles.voicePreviewLine}>
      <span className={styles.voicePreviewSpeaker}>{agent}</span>
      <span className={`${styles.voicePreviewText} ${muted ? styles.voicePreviewMuted : ""}`}>
        {withFields(text, values)}
      </span>
    </div>
  );

  return (
    <div className={`${styles.previewShell} ${inTabs ? styles.previewShellBare : ""}`}>
      {inTabs ? null : (
        <div className={styles.previewToolbar}>
          <span className={styles.previewTitle}>
            <PhoneOutlined /> {t("surveyManagement.voice_preview_title")}
          </span>
        </div>
      )}
      <p className={styles.previewCaption}>{t("surveyManagement.voice_preview_caption")}</p>
      <div className={styles.voicePreviewCall}>
        {line(
          "greeting",
          (greeting || "").trim() || t("surveyManagement.voice_preview_default_greeting"),
          !(greeting || "").trim()
        )}
        {(disclosure || "").trim() ? line("disclosure", disclosure.trim()) : null}
        {asked.length ? (
          asked.map((q, index) => {
            const hint = answerHint(q, t);
            return line(q.id || index, `${index + 1}. ${q.text.trim()}${hint ? ` ${hint}` : ""}`);
          })
        ) : (
          <div className={styles.voicePreviewMuted}>{t("surveyManagement.voice_preview_no_questions")}</div>
        )}
        {line(
          "goodbye",
          (thankYouMessage || "").trim() || t("surveyManagement.voice_preview_default_goodbye"),
          !(thankYouMessage || "").trim()
        )}
      </div>
      {inTabs ? null : <p className={styles.previewCaption}>{t("surveyManagement.voice_preview_try")}</p>}
    </div>
  );
};

export default VoiceSurveyPreview;
